export const useEventPartnersStore = defineStore('EventPartnersStore', () => {
    const {$api} = useNuxtApp();
    const partnersData: Ref<EventPartners|null> = ref(null);
    const partners: Ref<CompanyEventPartnership[]|null> = ref(null);
    const goodies: Ref<CompanyEventPartnershipGoodies[]|null> = ref(null);
    const activities: Ref<CompanyEventPartnershipBoothActivities[]|null> = ref(null);
    const dataLoaded: Ref<boolean> = ref(false);
    // Event whose partners are currently in the store; null until the first successful load.
    const loadedEventId: Ref<number|null> = ref(null);
    // Set when loadEventPartners fails with nothing cached to fall back on. Detail pages
    // wait on `dataLoaded || loadError` so they show a 503 "You're offline" instead of
    // hanging on `until(dataLoaded)` forever.
    const loadError: Ref<any|null> = ref(null);

    // In-flight request shared between concurrent callers (e.g. a page and a block mounting on
    // the same navigation), so they don't stack duplicate requests.
    let pending: Promise<void>|null = null;
    let pendingEventId: number|null = null;
    // Monotonic token so a slow response for a previous event can't overwrite newer data.
    let requestSeq = 0;

    const loadEventPartners = async(eventId: number): Promise<void> => {
        const seq = ++requestSeq;
        // Starting a request of our own retires whatever ensureLoaded is sharing: after a tab
        // freeze that promise belongs to a suspended request that will never settle, and handing
        // it to the next caller would hang them on it forever. Runs before ensureLoaded assigns
        // the new one below, because this body executes synchronously up to its first await.
        pending = null;
        pendingEventId = null;
        loadError.value = null;
        if(loadedEventId.value !== null && loadedEventId.value !== eventId) {
            // Switching events: drop the other event's data so pages waiting on `dataLoaded`
            // get this event's payload instead of rendering stale partners.
            loadedEventId.value = null;
            dataLoaded.value = false;
            partnersData.value = null;
            partners.value = null;
            goodies.value = null;
            activities.value = null;
        }
        // Not served while the event is in Program Preview (the API answers 403 too): nothing to ask for, nothing to
        // show, and no failed request for every page and reload that wants this data.
        const eventsStore = useEventsStore();
        if(eventsStore.eventData?.id === eventId && !eventsStore.communityAvailable) {
            partnersData.value = null;
            partners.value = [];
            goodies.value = [];
            activities.value = [];
            dataLoaded.value = true;
            loadedEventId.value = eventId;
            return;
        }
        try {
            const data = await $api.v2.getEventPartnersByEventId(eventId);
            if(seq !== requestSeq) return;
            partnersData.value = data;
            partners.value = data?.partners ?? [];
            goodies.value = data?.goodies ?? [];
            activities.value = data?.activities ?? [];
            dataLoaded.value = true;
            loadedEventId.value = eventId;
        } catch(error: any) {
            const err = createDataLoadError(error, 'Failed to load event partners data');
            // Only surface to blocked pages when we have nothing cached to render.
            if (seq === requestSeq && !dataLoaded.value) {
                loadError.value = err;
            }
            throw err;
        }
    }

    // Fetch-once-per-event entry point for pages/components that need partners data: no-op when
    // this event's data is already loaded, joins the in-flight request when one is running, and
    // only otherwise hits the API. A failed load is retried on the next call.
    const ensureLoaded = (eventId: number): Promise<void> => {
        if(loadedEventId.value === eventId && dataLoaded.value) {
            return Promise.resolve();
        }
        if(pending && pendingEventId === eventId) {
            return pending;
        }
        // Both assigned AFTER the call, because loadEventPartners clears them on the way in to
        // retire any suspended request; setting them first would leave pendingEventId null and
        // every later caller starting a request of its own. The .finally below only ever runs on
        // a later tick, so it still sees both.
        pending = loadEventPartners(eventId).finally(() => {
            // Guarded so a load for event A finishing late can't clear event B's in-flight state.
            if(pendingEventId === eventId) {
                pending = null;
                pendingEventId = null;
            }
        });
        pendingEventId = eventId;
        return pending;
    }

    return {
        partners,
        goodies,
        activities,
        dataLoaded,
        loadedEventId,
        loadError,
        // actions
        loadEventPartners,
        ensureLoaded,
    };

}, {},)
